/**
 * StreamLain DocId/BlockId Remapping Middleware
 *
 * sl-ht07.3: During snapshot import, replaceIdMiddleware remaps block IDs and
 * page IDs but does NOT touch docId/blockId references stored in custom props
 * of Automation, Ghost, and Database (relation column) blocks.
 *
 * This middleware:
 * 1. Tracks old→new ID mappings by observing beforeImport/afterImport events
 * 2. Provides remapStreamlainReferences() for post-import fixup
 *
 * MUST be placed BEFORE replaceIdMiddleware in the middlewares array
 * so beforeImport handlers capture original IDs before mutation.
 */

import type {
  AfterImportBlockPayload,
  BeforeImportBlockPayload,
  BlockSnapshot,
  DocSnapshot,
  Store,
  TransformerMiddleware,
} from '@blocksuite/store';
import { filter } from 'rxjs';

// ─── Middleware: ID Tracker ──────────────────────────────────────────────────

/**
 * Creates a middleware that passively tracks old→new ID mappings.
 * The maps are populated during import and consumed by remapStreamlainReferences().
 */
export function createStreamlainIdTracker(
  docIdMap: Map<string, string>,
  blockIdMap: Map<string, string>
): TransformerMiddleware {
  return ({ slots }) => {
    const originalPageIds = new WeakMap<DocSnapshot, string>();
    const originalBlockIds = new WeakMap<BlockSnapshot, string>();

    // Capture original page IDs BEFORE replaceIdMiddleware mutates them
    const beforePageSub = slots.beforeImport
      .pipe(filter(p => p.type === 'page'))
      .subscribe(payload => {
        const snapshot = (payload as { snapshot: DocSnapshot }).snapshot;
        originalPageIds.set(snapshot, snapshot.meta.id);
      });

    // Capture original block IDs BEFORE replaceIdMiddleware mutates them
    const beforeBlockSub = slots.beforeImport
      .pipe(filter((p): p is BeforeImportBlockPayload => p.type === 'block'))
      .subscribe(payload => {
        originalBlockIds.set(payload.snapshot, payload.snapshot.id);
      });

    // Build docIdMap from observed page ID changes
    const afterPageSub = slots.afterImport
      .pipe(filter(p => p.type === 'page'))
      .subscribe(payload => {
        const { snapshot, page } = payload as {
          snapshot: DocSnapshot;
          page: Store;
        };
        const oldId = originalPageIds.get(snapshot);
        if (oldId && oldId !== page.id) {
          docIdMap.set(oldId, page.id);
        }
      });

    // Build blockIdMap from observed block ID changes
    const afterBlockSub = slots.afterImport
      .pipe(filter((p): p is AfterImportBlockPayload => p.type === 'block'))
      .subscribe(payload => {
        const oldId = originalBlockIds.get(payload.snapshot);
        if (oldId && oldId !== payload.model.id) {
          blockIdMap.set(oldId, payload.model.id);
        }
      });

    return () => {
      beforePageSub.unsubscribe();
      beforeBlockSub.unsubscribe();
      afterPageSub.unsubscribe();
      afterBlockSub.unsubscribe();
    };
  };
}

// ─── Post-Import Remapping ───────────────────────────────────────────────────

/**
 * Remaps StreamLain-specific docId and blockId references in imported docs.
 * Call AFTER all docs have been imported (Promise.all resolved) so both maps
 * are fully populated.
 */
export function remapStreamlainReferences(
  stores: Store[],
  docIdMap: Map<string, string>,
  blockIdMap: Map<string, string>
): void {
  if (docIdMap.size === 0 && blockIdMap.size === 0) return;

  for (const store of stores) {
    const root = store.root;
    if (!root) continue;
    walkBlocks(root, block => {
      if (block.flavour === 'affine:automation') {
        remapAutomationProps(block, docIdMap, blockIdMap);
      } else if (block.flavour === 'affine:ghost') {
        remapGhostProps(block, docIdMap, blockIdMap);
      } else if (block.flavour === 'affine:database') {
        remapDatabaseRelationProps(block, docIdMap, blockIdMap);
      }
    });
  }
}

// ─── Block Walker ────────────────────────────────────────────────────────────

interface BlockLike {
  flavour: string;
  props: Record<string, unknown>;
  children: BlockLike[];
}

function walkBlocks(block: BlockLike, fn: (b: BlockLike) => void): void {
  fn(block);
  for (const child of block.children) {
    walkBlocks(child, fn);
  }
}

// ─── Remap Helpers ───────────────────────────────────────────────────────────

function remapId(
  id: string | null | undefined,
  map: Map<string, string>
): string | null | undefined {
  if (!id) return id;
  return map.get(id) ?? id;
}

function remapAutomationProps(
  block: BlockLike,
  docIdMap: Map<string, string>,
  blockIdMap: Map<string, string>
): void {
  const p = block.props;

  // DocId fields
  p.sourceDocId = remapId(p.sourceDocId as string | null, docIdMap);

  // BlockId fields
  p.sourceTableId = remapId(p.sourceTableId as string | null, blockIdMap);

  // inputTables[]
  const inputTables = p.inputTables as
    | Array<{ tableId?: string; docId?: string }>
    | undefined;
  if (inputTables) {
    for (const t of inputTables) {
      t.tableId = remapId(t.tableId, blockIdMap) as string;
      t.docId = remapId(t.docId, docIdMap) as string | undefined;
    }
  }

  // outputTables[]
  const outputTables = p.outputTables as
    | Array<{ tableId?: string; docId?: string }>
    | undefined;
  if (outputTables) {
    for (const t of outputTables) {
      t.tableId = remapId(t.tableId, blockIdMap) as string;
      t.docId = remapId(t.docId, docIdMap) as string | undefined;
    }
  }

  // outputMappings[].tableId
  const outputMappings = p.outputMappings as
    | Array<{ tableId?: string }>
    | undefined;
  if (outputMappings) {
    for (const m of outputMappings) {
      m.tableId = remapId(m.tableId, blockIdMap) as string;
    }
  }

  // matchConfigs[].targetTableId
  const matchConfigs = p.matchConfigs as
    | Array<{ targetTableId?: string }>
    | undefined;
  if (matchConfigs) {
    for (const mc of matchConfigs) {
      mc.targetTableId = remapId(mc.targetTableId, blockIdMap) as string;
    }
  }
}

function remapGhostProps(
  block: BlockLike,
  docIdMap: Map<string, string>,
  blockIdMap: Map<string, string>
): void {
  const p = block.props;

  // DocId fields
  p.targetDocId = remapId(p.targetDocId as string, docIdMap);

  // BlockId fields
  p.targetDatabaseId = remapId(p.targetDatabaseId as string, blockIdMap);
  p.sourceDatabaseId = remapId(p.sourceDatabaseId as string, blockIdMap);
  p.sourceElementId = remapId(p.sourceElementId as string, blockIdMap);
}

function remapDatabaseRelationProps(
  block: BlockLike,
  docIdMap: Map<string, string>,
  blockIdMap: Map<string, string>
): void {
  // Database columns are in props.columns
  const columns = block.props.columns as
    | Array<{ type?: string; data?: Record<string, unknown> }>
    | undefined;
  if (!columns) return;

  for (const col of columns) {
    if (col.type !== 'relation' || !col.data) continue;

    col.data.targetDocId = remapId(
      col.data.targetDocId as string | undefined,
      docIdMap
    );
    col.data.targetDatabaseId = remapId(
      col.data.targetDatabaseId as string | undefined,
      blockIdMap
    );
  }
}
